import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchConflictGroupTransactions,
  fetchConflictGroups,
  fetchConflictsSummary,
  resolveConflict,
  resolveConflictGroup,
} from '../api/conflicts-api'
import type { ConflictAction, ConflictResolution } from '../types/tagging'
import { usePermissions } from '@/shared/lib/permissions/use-permissions'
import { Permissions, PermissionLevel } from '@/shared/lib/permissions/permission-values'
import { taggingKeys } from './use-tagging-queries'

export const conflictKeys = {
  groups: ['tagging', 'conflicts', 'groups'] as const,
  summary: ['tagging', 'conflicts', 'summary'] as const,
  sample: (classifyingRuleId: number, contenderRuleId: number) =>
    ['tagging', 'conflicts', 'sample', classifyingRuleId, contenderRuleId] as const,
}

// Anything a resolution changes: the inbox, open side panels and the grid's Category column.
function useRefreshAfterResolution() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tagging', 'conflicts'] }),
      queryClient.invalidateQueries({ queryKey: taggingKeys.classifications }),
      queryClient.invalidateQueries({ queryKey: ['Transactions', 'list'] }),
    ])
}

// The engine records conflicts every minute; the badge does not need to be fresher than that.
const SUMMARY_REFRESH_MS = 60 * 1000

/** The rules page banner and the menu counter (UC-086). */
export function useConflictsSummary(enabled = true) {
  return useQuery({
    queryKey: conflictKeys.summary,
    queryFn: ({ signal }) => fetchConflictsSummary(signal),
    refetchInterval: SUMMARY_REFRESH_MS,
    staleTime: SUMMARY_REFRESH_MS,
    enabled,
  })
}

/**
 * The menu counter on Classification conflicts: how many rule pairs disagree. The menu renders it for every user
 * (on the item, its section and the menu button), so it asks only for whoever may read tagging; zero shows nothing.
 */
export function useOpenConflictPairsCount(): number | undefined {
  const { can } = usePermissions()
  const { data } = useConflictsSummary(can({ name: Permissions.Tags, level: PermissionLevel.Read }))
  return data?.openGroups
}

export function useConflictGroups() {
  return useQuery({ queryKey: conflictKeys.groups, queryFn: ({ signal }) => fetchConflictGroups(signal) })
}

export function useConflictGroupSample(classifyingRuleId: number, contenderRuleId: number, enabled: boolean) {
  return useQuery({
    queryKey: conflictKeys.sample(classifyingRuleId, contenderRuleId),
    queryFn: ({ signal }) => fetchConflictGroupTransactions(classifyingRuleId, contenderRuleId, signal),
    enabled,
  })
}

/** Resolves a whole group. "Switch" is applied in slices by the backend; this keeps calling until none remain. */
export function useResolveConflictGroup() {
  const refresh = useRefreshAfterResolution()
  return useMutation({
    mutationFn: async ({ classifyingRuleId, contenderRuleId, action }: {
      classifyingRuleId: number
      contenderRuleId: number
      action: ConflictAction
    }): Promise<ConflictResolution> => {
      let total = 0
      let result = await resolveConflictGroup(classifyingRuleId, contenderRuleId, action)
      total += result.resolved
      while (action === 'Switch' && result.remaining > 0) {
        result = await resolveConflictGroup(classifyingRuleId, contenderRuleId, action)
        total += result.resolved
      }
      return { resolved: total, remaining: result.remaining }
    },
    onSuccess: refresh,
  })
}

export function useResolveConflict() {
  const refresh = useRefreshAfterResolution()
  return useMutation({
    mutationFn: ({ conflictId, action }: { conflictId: number; action: 'Keep' | 'Switch' }) => resolveConflict(conflictId, action),
    onSuccess: refresh,
  })
}
